/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Injectable, OnDestroy } from '@angular/core';
import * as Stomp from 'stompjs';
import * as SockJS from 'sockjs-client';
import { ReplaySubject, Subscription } from 'rxjs';
import { LocalStorageService, SessionStorageService } from 'ngx-webstorage';
import { StompSendRequestModel } from './stomp-send-request.model';

@Injectable()
export abstract class WellStompService implements OnDestroy {
  stompCallbacks: ReplaySubject<any> = new ReplaySubject();
  stompSendRequestsQueue: ReplaySubject<StompSendRequestModel> = new ReplaySubject();
  stompCallbacksSubscription: Subscription | undefined = undefined;

  stompClient: any | undefined = undefined;

  protected constructor(private serverUrl: string,
                        private $localStorage: LocalStorageService,
                        private $sessionStorage: SessionStorageService) {
  }

  ngOnDestroy(): void {
    this.stompTopicsUnsubscribe();
    this.disconnect();
  }

  abstract stompTopicsUnsubscribe(): void;

  protected abstract stompTopicsSubscribe(args?: any): void;

  public initializeConnectionAndSubscribeTopics(args?: any){
    this.initializeWebSocketConnection();
    this.stompTopicsSubscribe(args)
  }

  private initializeWebSocketConnection() {
    if (!this.stompClient && !this.stompCallbacksSubscription) {
      const token = this.$localStorage.retrieve('authenticationToken') || this.$sessionStorage.retrieve('authenticationToken');
      const ws = new SockJS(`${this.serverUrl}?access_token=${token}`);
      this.stompClient = Stomp.over(ws);
      this.stompClient.debug = null;

      this.stompClient.connect({Authorization: `Bearer ${token}`}, (frame: any) => {

        this.stompCallbacksSubscription = this.stompCallbacks.subscribe(
          callback => {
            callback(this.stompClient);
          }
        );

        this.stompSendRequestsQueue.subscribe(res => {
          this.sendRaw(res.topicUrl, res.payload);
        });
        this.stompSendRequestsQueue.complete();

      });
    }
  }

  protected disconnect() {
    if (this.stompCallbacksSubscription && this.stompClient) {
      if (!this.stompCallbacksSubscription.closed) {
        try {
          this.stompClient.disconnect(() => {
            this.stompCallbacksSubscription?.unsubscribe();
            this.stompCallbacks = new ReplaySubject();
            this.stompSendRequestsQueue = new ReplaySubject();
          });
        } catch (e) {
          console.error(e);
        }
      }
    }
  }

  register(callback: any) {
    this.stompCallbacks.next(callback);
  }

  send(topicUrl: string, data: any) {
    if (!this.stompCallbacksSubscription && this.stompClient) {
      this.stompSendRequestsQueue.next({topicUrl: topicUrl, payload: JSON.stringify(data)});
    } else {
      this.stompClient.send(topicUrl, {}, JSON.stringify(data));
    }
  }

  sendRaw(topicUrl: string, data: any) {
    if (!this.stompCallbacksSubscription && this.stompClient) {
      this.stompSendRequestsQueue.next({topicUrl: topicUrl, payload: data});
    } else {
      this.stompClient.send(topicUrl, {}, data);
    }
  }
}

